'use client';

import { useEffect } from 'react';
import useSWR from 'swr';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAppContext } from 'src/context/app';
import { useAuthContext } from 'src/auth/hooks';
import { hämtaKomIgångStatus } from 'src/sections/kom-igang/KomIgangService';
import type { KomIgångStatus } from 'src/sections/kom-igang/kom-igang-typer';
import { endpoints } from 'src/lib/axios';
import { useStartsidaVal } from 'src/sections/dashboard/components/startsida-valjare';
import { FöreningsDashboard } from 'src/sections/dashboard/view/app-dashboard-forening';
import { AllaOrganisationerHubb } from 'src/sections/dashboard/view/alla-organisationer-hubb';

// ----------------------------------------------------------------------

const MINSTA_KLARA_STEG = 5;

export default function AppensÖversikt() {
  const { user } = useAuthContext();
  const { organisation, ärAllaOrganisationer } = useAppContext();
  const router = useRouter();

  const orgId = organisation?.id;
  const { startsida: sparat, laddar: inställningLaddar } = useStartsidaVal(orgId);

  const { data: komIgångStatus, isLoading: komIgångLaddar } = useSWR<KomIgångStatus>(
    orgId && !inställningLaddar && sparat === null
      ? endpoints.komIgång.status
      : null,
    hämtaKomIgångStatus,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (user && user.emailVerified === false) {
      router.replace(paths.VERIFIERA);
    }
  }, [user, router]);

  useEffect(() => {
    if (!orgId || ärAllaOrganisationer || inställningLaddar) return;

    if (sparat === 'kom-igang') {
      router.replace(paths.dashboard.komIgång);
      return;
    }

    if (sparat === 'oversikt') return;

    if (komIgångLaddar || !komIgångStatus) return;
    const klaraSteg =
      (komIgångStatus.avbockade?.length ?? 0) + (komIgångStatus.härledda?.length ?? 0);
    if (klaraSteg < MINSTA_KLARA_STEG) {
      router.replace(paths.dashboard.komIgång);
    }
  }, [orgId, ärAllaOrganisationer, inställningLaddar, sparat, komIgångLaddar, komIgångStatus, router]);

  if (ärAllaOrganisationer) return <AllaOrganisationerHubb />;

  if (!orgId || inställningLaddar) return null;

  if (sparat === 'kom-igang') return null;

  if (sparat === null) {
    if (komIgångLaddar || !komIgångStatus) return null;
    const klaraSteg =
      (komIgångStatus.avbockade?.length ?? 0) + (komIgångStatus.härledda?.length ?? 0);
    if (klaraSteg < MINSTA_KLARA_STEG) return null;
  }

  return <FöreningsDashboard />;
}
